/**
 * BHT-7374 established this boundary. The quote API's
 * `ValidationError.message` is the raw backend exception string passed
 * through from listing_engine, which wraps the booking channel's error.
 * It can include internal ids, host-facing instructions, and the full
 * request payload, e.g.:
 *
 *   ("Quote exception ('Requested occupancy of adults=5 and children=2
 *   is greater than the total allowed occupancy of 6', -1); nid: 280413,
 *   payload: {'channel': 'bnbfinder', 'channel_id': '728b...', ...}", -1)
 *
 * Nothing raw may reach the user. This is the single boundary that
 * translates the known validation classes into house copy; anything
 * unrecognized gets the generic fallback, and `matched: false` so the
 * caller can report it (BHT-7375).
 *
 * BHT-7392 widened it past RedAwning. Every channel phrases the same
 * conditions differently, so the rules below group by condition with one
 * entry per observed vendor phrasing. A channel can nest a specific reason
 * inside a generic wrapper, so the specific rules have to run first.
 *
 * The durable fix is structured error codes from listing_engine, which
 * already knows a reason atom and a vendor status code per class and
 * throws both away when it builds the message (BHT-7393). Once codes
 * arrive, this table keys off them and the patterns disappear.
 */

export const GENERIC_QUOTE_VALIDATION_MESSAGE =
  "These dates aren't available. Try adjusting your dates or guest count.";

const DATES_UNAVAILABLE =
  "These dates aren't available for this home. Try different dates.";

const ALREADY_BOOKED = 'These dates are already booked. Try different dates.';

const NOT_ACCEPTING_BOOKINGS =
  "This home isn't accepting bookings right now. Try another home for these dates.";

const CHECK_IN_TOO_SOON =
  "Check-in this soon isn't available online. Please choose a later check-in date.";

const GUEST_COUNT_TOO_HIGH =
  "This home can't sleep that many guests. Reduce your guest count to book.";

const SELECTED_PETS_NOT_ALLOWED =
  "This home doesn't allow the pets you selected. Remove pets from your search to book.";

const WEEKDAY_NAMES: Record<string, string> = {
  sun: 'Sunday',
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
};

export type QuoteValidationCopy = {
  /** User-facing copy, never the raw string */
  message: string;
  /** False when no known pattern matched and the generic fallback is used */
  matched: boolean;
};

type QuoteValidationRule = {
  pattern: RegExp;
  copy: string | ((match: RegExpMatchArray) => string);
};

const guestCapacityCopy = (match: RegExpMatchArray) =>
  `This home sleeps up to ${match[1]} guests. Reduce your guest count to book.`;

const stayLengthCopy =
  (bound: 'Minimum' | 'Maximum') => (match: RegExpMatchArray) => {
    const nights = Number(match[1]);
    const unit = nights === 1 ? 'night' : 'nights';
    return `${bound} stay is ${nights} ${unit} for these dates.`;
  };

const minimumStayCopy = stayLengthCopy('Minimum');
const maximumStayCopy = stayLengthCopy('Maximum');

const PET_SETTINGS_PATTERN =
  /Pet policy violation:.*Unit settings - cats = '(True|False)' dogs = '(True|False)'/;

/**
 * The unit's own cat and dog flags are the only part of the raw string safe
 * to read out, and naming the species the home does take is the only
 * actionable detail in it.
 */
const petPolicyCopy = (match: RegExpMatchArray) => {
  const [, catsAllowed, dogsAllowed] = match;
  if (catsAllowed === 'False' && dogsAllowed === 'False') {
    return "This home doesn't allow pets. Remove pets from your search to book.";
  }
  if (dogsAllowed === 'True' && catsAllowed === 'False') {
    return 'This home allows dogs but not cats. Remove pets from your search to book.';
  }
  if (catsAllowed === 'True' && dogsAllowed === 'False') {
    return 'This home allows cats but not dogs. Remove pets from your search to book.';
  }
  return SELECTED_PETS_NOT_ALLOWED;
};

/**
 * Platform copy from listing_engine's booking lead time guard
 * (GraphWeb.BookingLeadTimeErrors.check_in_too_soon/1, BHT-8031), not
 * vendor prose. The copy is rebuilt from the captured date rather than
 * echoed, so only this sentence shape can ever reach the guest; any
 * appended tail fails the full-message anchor and falls to the generic
 * fallback. The date grammar matches strftime "%B %-d, %Y" exactly (the
 * day is not zero-padded).
 */
const platformLeadTimeCopy = (match: RegExpMatchArray) =>
  `This check-in date is too soon to book. The earliest check-in date you can book is ${match[1]}.`;

/**
 * Built from the map keys so the alternation and the lookup cannot drift
 * apart. Editing one abbreviation out of the map takes it out of the pattern
 * too, rather than leaving a rule that matches and then renders "undefined".
 */
const CHECK_IN_WEEKDAY_PATTERN = new RegExp(
  `can only occur on (${Object.keys(WEEKDAY_NAMES).join('|')})\\b`,
  'i'
);

const checkInWeekdayCopy = (match: RegExpMatchArray) => {
  const weekday = WEEKDAY_NAMES[match[1].toLowerCase()];
  return `This home only accepts ${weekday} check-ins. Move your check-in to a ${weekday} to book.`;
};

/**
 * Evaluated top to bottom, first match wins. The test fixtures name the
 * channel each phrasing was captured from.
 */
const QUOTE_VALIDATION_RULES: QuoteValidationRule[] = [
  // Over occupancy.
  { pattern: /allowed occupancy of (\d+)/, copy: guestCapacityCopy },
  {
    pattern: /exceeds max occupancy for unit \((\d+)\)/,
    copy: guestCapacityCopy,
  },
  { pattern: /The number of people is too high/, copy: GUEST_COUNT_TOO_HIGH },

  // Stay too short. Lodgify counts in days, which is nights on its API.
  { pattern: /minimum stay (?:is|of) (\d+) night/, copy: minimumStayCopy },
  { pattern: /minimum length of stay = '(\d+)'/, copy: minimumStayCopy },
  {
    pattern: /minimum stay for this rental is (\d+) day/,
    copy: minimumStayCopy,
  },

  // Stay too long.
  { pattern: /maximum stay is (\d+) night/, copy: maximumStayCopy },
  {
    pattern: /maximum stay for this house is (\d+) day/,
    copy: maximumStayCopy,
  },

  // Check-in restrictions. The first is our own platform rejection, the
  // rest are vendor phrasings. Its safety comes from the full-message
  // anchor, not from position: no other rule matches this sentence, so
  // its placement is order-independent today.
  {
    pattern:
      /^This check-in date is too soon to book\. The earliest check-in date you can book is ([A-Z][a-z]+ \d{1,2}, \d{4})\.$/,
    copy: platformLeadTimeCopy,
  },
  { pattern: /check-in is within \d+ hours/, copy: CHECK_IN_TOO_SOON },
  { pattern: CHECK_IN_WEEKDAY_PATTERN, copy: checkInWeekdayCopy },

  // Pets. The first rule needs the settings pair; the second catches drift.
  { pattern: PET_SETTINGS_PATTERN, copy: petPolicyCopy },
  { pattern: /^Pet policy violation/, copy: SELECTED_PETS_NOT_ALLOWED },

  // Not bookable at all. Escapia nests its version inside the unavailable
  // wrapper below, so these have to run first.
  { pattern: /this listing is not active/, copy: NOT_ACCEPTING_BOOKINGS },
  { pattern: /Set a rate to continue/, copy: NOT_ACCEPTING_BOOKINGS },
  { pattern: /Rate settings were not found/, copy: NOT_ACCEPTING_BOOKINGS },

  // Dates taken. Fires through shared links and stale pages whose dates have
  // since been booked; the calendar itself blocks unavailable dates.
  { pattern: /already booked for days/, copy: ALREADY_BOOKED },
  { pattern: /already booked on these dates/, copy: ALREADY_BOOKED },

  // Dates blocked for a reason the channel does not name. Least specific, last.
  // The two Track phrasings are its v2 quote endpoint's NOT_AVAILABLE and
  // RATE_INVALID answers (BHT-8249). RATE_INVALID is a span the home is open
  // for but has no rate covering it, such as a stay under the minimum, so it
  // belongs here and not with the not-bookable rules above.
  { pattern: /is unavailable for these dates/, copy: DATES_UNAVAILABLE },
  {
    pattern: /Period is (?:closed|occupied|in preparation|marked as)/i,
    copy: DATES_UNAVAILABLE,
  },
  {
    pattern: /unit or unit type is not available for the dates provided/,
    copy: DATES_UNAVAILABLE,
  },
  {
    pattern: /Expected rate type is not found or is incomplete/,
    copy: DATES_UNAVAILABLE,
  },
];

export function friendlyQuoteValidationMessage(
  rawMessage: string
): QuoteValidationCopy {
  for (const { pattern, copy } of QUOTE_VALIDATION_RULES) {
    const match = rawMessage.match(pattern);
    if (match) {
      return {
        message: typeof copy === 'string' ? copy : copy(match),
        matched: true,
      };
    }
  }

  return { message: GENERIC_QUOTE_VALIDATION_MESSAGE, matched: false };
}

/**
 * The reportable core of a raw validation message, sized for GA4's
 * 100-character event param limit. Prefers the clause inside the
 * "Quote exception ('...')" wrapper (the human-meaningful part) over
 * the head of the full tuple string, which would waste the budget on
 * wrapper punctuation. Escapia and Lodgify send bare prose, which both
 * branches leave alone.
 */
export function quoteValidationReportClause(rawMessage: string): string {
  const clause =
    rawMessage.match(/Quote exception \('(.+?)', -1\)/)?.[1] ??
    rawMessage.replace(/^[("']+/, '').replace(/["']?, -1\)+$/, '');
  return clause.slice(0, 100);
}
